import {
  Combobox,
  DropdownButton,
  NoOptionsPlaceholder
} from '@eggplant/pattern-library'
import { FiPlus } from 'react-icons/fi'
import { GetModelerTestCaseNamesRequest } from '@eggplant/api-requests'
import { HiOutlineClipboardCheck } from 'react-icons/hi'
import {
  LinkToAddTestCase,
  useSpaceAccess
} from '@eggplant/routing'
import type {
  ButtonShape,
  DropdownPlacement,
  GetFilterIdListResponse,
  Options
} from '@eggplant/types'

interface Props {
  onChange: (value: string) => void
  placeholder?: string
  value?: string
  dropdownPlacement?: DropdownPlacement
  isFilterFixedMinMaxWidth?: boolean
}

const getOptions = (items: GetFilterIdListResponse[] | undefined) => {
  if (!Array.isArray(items)) return []

  return items.map(item => ({
    label: item.name,
    value: item.id_list.join(',')
  }))
}

const SelectModelSuiteTestCases = ({
  onChange,
  placeholder = 'Test case',
  value,
  dropdownPlacement = 'bottom',
  isFilterFixedMinMaxWidth = true
}: Props) => {
  const [isAccessible] = useSpaceAccess()

  const renderButton = (options: Options) => {
    const currentOption = value
      ? options.find(item => item.value.includes(value))
      : undefined

    const label = currentOption?.value
      ? currentOption.label
      : placeholder

    const tooltip = currentOption?.value
      ? `Test case: ${currentOption.label}`
      : label

    const buttonText = currentOption?.value
      ? currentOption.label
      : placeholder

    return (buttonProps: ButtonShape) =>
      <DropdownButton
        {...buttonProps}
        Icon={HiOutlineClipboardCheck}
        dropdownPlacement={dropdownPlacement}
        isFilter
        isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
        isHighlighted={!!value}
        label={label}
        title={tooltip}
      >
        {buttonText}
      </DropdownButton>
  }

  const noDataPlaceholder =
    <NoOptionsPlaceholder
      icon={<HiOutlineClipboardCheck size='24px' />}
      linkTo={
        isAccessible
          ? (
            <LinkToAddTestCase>
              <FiPlus
                aria-hidden
                size={16}
              />
              New Test Case
            </LinkToAddTestCase>
          )
          : undefined
      }
      title='No test cases found'
    />

  return (
    <GetModelerTestCaseNamesRequest shortErrorMessage='Failed to load Test case'>
      {
        (data: GetFilterIdListResponse[]) =>
          <Combobox
            dropdownPlacement={dropdownPlacement}
            isFilter
            isFilterFixedMinMaxWidth={isFilterFixedMinMaxWidth}
            noOptionsPlaceholder={noDataPlaceholder}
            onChange={onChange}
            options={getOptions(data)}
            renderButton={renderButton(getOptions(data))}
            value={value}
          />
      }
    </GetModelerTestCaseNamesRequest>
  )
}

export default SelectModelSuiteTestCases
